// src/pages/Reports.js — Admin Portal report generation
import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { get } from '../utils/api';
import { Spinner, PageHeader, formatCurrency } from '../components/Shared';

function localDateString(d) {
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().split('T')[0];
}
const today   = () => localDateString(new Date());
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return localDateString(d); };

const STAGE_COLORS = {
  stage_1_erp:           { bg: '#e5e7eb', text: '#374151', label: 'ERP Received' },
  stage_2_stock_removal: { bg: '#fef3c7', text: '#92400e', label: 'Stock Removal' },
  stage_3_checking:      { bg: '#dbeafe', text: '#1d4ed8', label: 'Checking' },
  stage_4_packing:       { bg: '#f3e8ff', text: '#7e22ce', label: 'Ready for Delivery' },
  stage_5_dispatched:    { bg: '#e0e7ff', text: '#4338ca', label: 'In Transit' },
  stage_6_delivered:     { bg: '#dcfce7', text: '#166534', label: 'Delivered' },
  reattempt_pending:     { bg: '#ffedd5', text: '#9a3412', label: 'Reattempt' },
  cancelled:             { bg: '#fee2e2', text: '#991b1b', label: 'Cancelled' },
  complete:              { bg: '#d1fae5', text: '#065f46', label: 'Complete' },
};
function stageStyle(status) {
  return STAGE_COLORS[status] || { bg: '#f3f4f6', text: '#4b5563', label: (status || '—').replace(/_/g, ' ') };
}

const REPORT_TYPES = [
  { value: 'date_wise',   label: '📅 Date Wise',   desc: 'Activity broken down day by day' },
  { value: 'party_wise',  label: '🏢 Party Wise',  desc: 'Invoices grouped by customer' },
  { value: 'staff_wise',  label: '👤 Staff Wise',  desc: 'Work done by individual team members' },
  { value: 'trip_report', label: '🚦 Round Report', desc: 'Delivery person round-by-round summaries' },
];

function formatDuration(seconds) {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', weekday: 'short' });
}

function buildCSVString(headers, rows) {
  return [
    headers.join(','),
    ...rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
  ].join('\n');
}
function previewCSV(headers, rows) {
  const csv = buildCSVString(headers, rows);
  const blob = new Blob([csv], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}

function downloadXLSX(headers, rows, filename, textColumns = []) {
  const wsData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  textColumns.forEach(colIdx => {
    for (let r = 1; r < wsData.length; r++) {
      const cellRef = XLSX.utils.encode_cell({ r, c: colIdx });
      const cell = ws[cellRef];
      if (cell) cell.t = 's';
    }
  });

  const colWidths = headers.map((h, colIdx) => {
    let maxLen = String(h ?? '').length;
    rows.forEach(row => {
      const len = String(row[colIdx] ?? '').length;
      if (len > maxLen) maxLen = len;
    });
    return { wch: Math.min(maxLen + 2, 40) };
  });
  ws['!cols'] = colWidths;
  // Best-effort A4 print margins — the free/community build of the xlsx
  // library doesn't reliably support embedding page-size/fit-to-page
  // settings, so this alone isn't a guaranteed A4 fit. Use the "Print
  // Report" button instead for a print output that's actually guaranteed
  // to come out correctly on A4 — it uses the browser's own print engine
  // directly rather than depending on Excel's handling of this file.
  ws['!margins'] = { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, filename);
}

function printReportA4(title, headers, rows) {
  const win = window.open('', '_blank');
  const rowsHtml = rows.map(r =>
    `<tr>${r.map(v => `<td>${String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;')}</td>`).join('')}</tr>`
  ).join('');
  const headHtml = headers.map(h => `<th>${h}</th>`).join('');
  win.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        @page { size: A4; margin: 12mm; }
        * { box-sizing: border-box; }
        body { font-family: Arial, sans-serif; font-size: 10px; color: #111; margin: 0; }
        h1 { font-size: 15px; margin: 0 0 4px 0; }
        p.meta { font-size: 10px; color: #666; margin: 0 0 14px 0; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; word-break: break-word; }
        th { background: #f3f4f6; font-weight: 700; }
        tr { page-break-inside: avoid; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style>
    </head>
    <body>
      <h1>${title}</h1>
      <p class="meta">Generated ${new Date().toLocaleString('en-IN')} — Mangal Distributors</p>
      <table>
        <thead><tr>${headHtml}</tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <script>window.onload = () => { window.print(); };</script>
    </body>
    </html>
  `);
  win.document.close();
}

export default function Reports() {
  const [reportType, setReportType] = useState('date_wise');
  const [from,       setFrom]       = useState(today());
  const [to,         setTo]         = useState(today());
  const [users,      setUsers]      = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [generated,  setGenerated]  = useState(false);
  const [sortCol,    setSortCol]    = useState(null);
  const [sortOrder,  setSortOrder]  = useState('asc');
  const [partyNameFilter, setPartyNameFilter] = useState('');
  const [deliveryType, setDeliveryType] = useState(''); // '' | 'instation' | 'outstation'

  function handleSort(col) {
    if (col === sortCol) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortOrder('asc'); }
  }

  useEffect(() => {
    async function loadUsers() {
      try {
        const res = await get('/api/users?limit=200');
        setUsers(res.users || []);
      } catch (e) { console.error(e); }
    }
    loadUsers();
  }, []);

  function toggleUser(id) {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  const generate = useCallback(async () => {
    setLoading(true);
    setData(null);
    setGenerated(false);
    try {
      if (reportType === 'trip_report') {
        let url = `/api/deliveries/trips?from=${from}&to=${to}`;
        if (selectedUsers.length === 1) url += `&user_id=${selectedUsers[0]}`;
        const res = await get(url);
        setData(res.trips || []);
      } else {
        let url = `/api/invoices?limit=2000&offset=0&invoice_date_from=${from}&invoice_date_to=${to}`;
        if (deliveryType) url += `&delivery_type=${encodeURIComponent(deliveryType)}`;
        if (reportType === 'staff_wise' && selectedUsers.length > 0) {
          url += `&handled_by=${selectedUsers.join(',')}`;
        }
        const res = await get(url);
        setData(res.invoices || []);
      }
      setGenerated(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [reportType, from, to, selectedUsers, deliveryType]);

  function getDateWiseData() {
    const grouped = {};
    (data || []).forEach(inv => {
      const date = inv.received_at ? new Date(inv.received_at).toISOString().split('T')[0] : 'Unknown';
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(inv);
    });
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  }

  function getPartyWiseData() {
    const grouped = {};
    (data || []).forEach(inv => {
      const key = inv.party_name || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(inv);
    });
    let entries = Object.entries(grouped);
    if (partyNameFilter.trim()) {
      const q = partyNameFilter.trim().toLowerCase();
      entries = entries.filter(([party]) => party.toLowerCase().includes(q));
    }
    return entries.sort((a, b) => b[1].length - a[1].length);
  }

  function buildReportData() {
    if (reportType === 'trip_report') {
      const headers = ['Round #', 'Delivery Person', 'Phone', 'Invoice #', 'Party', 'City', 'Outcome', 'Voucher Status', 'Date', 'Round Start', 'Round End', 'Duration', 'Total Invoices', 'Delivered', 'Reattempts', 'Cancelled'];
      const rows = (data || []).flatMap(t =>
        (t.invoices && t.invoices.length ? t.invoices : [{}]).map(inv => [
          t.round_number, t.delivery_person_name, t.delivery_person_phone || '',
          inv.invoice_number || '', inv.party_name || '', inv.city || '',
          inv.outcome || '', (inv.voucher_returned && inv.voucher_status) ? inv.voucher_status.replace(/_/g, ' ') : '',
          String(t.trip_date).split('T')[0],
          formatDateTime(t.trip_start), t.trip_end ? formatDateTime(t.trip_end) : 'In progress',
          formatDuration(t.trip_duration_seconds),
          t.total_invoices, t.delivered, t.reattempts, t.cancelled,
        ])
      );
      return { headers, rows, textColumns: [2] };
    } else if (reportType === 'date_wise') {
      const headers = ['Date', 'Invoice #', 'Party', 'City', 'Stage', 'Value', 'Articles', 'Transport'];
      const isDelivered = (inv) => ['stage_6_delivered', 'complete'].includes(inv.status);
      const rows = getDateWiseData().flatMap(([date, invs]) =>
        invs.map(inv => [date, inv.invoice_number, inv.party_name, inv.city, stageStyle(inv.status).label, inv.invoice_value,
          isDelivered(inv) ? (inv.article_count ?? '') : '',
          isDelivered(inv) ? (inv.transport_name ?? '') : ''])
      );
      return { headers, rows, textColumns: [] };
    } else if (reportType === 'party_wise') {
      const headers = ['Party', 'City', 'Invoice #', 'Date', 'Stage', 'Value', 'Articles', 'Transport'];
      const isDelivered2 = (inv) => ['stage_6_delivered', 'complete'].includes(inv.status);
      const rows = getPartyWiseData().flatMap(([party, invs]) =>
        invs.map(inv => [party, inv.city, inv.invoice_number, inv.received_at?.split('T')[0], stageStyle(inv.status).label, inv.invoice_value,
          isDelivered2(inv) ? (inv.article_count ?? '') : '',
          isDelivered2(inv) ? (inv.transport_name ?? '') : ''])
      );
      return { headers, rows, textColumns: [] };
    } else {
      const headers = ['Invoice #', 'Party', 'City', 'Stage', 'Handler', 'Value', 'Date', 'Articles', 'Transport'];
      const isDelivered3 = (inv) => ['stage_6_delivered', 'complete'].includes(inv.status);
      const rows = (data || []).map(inv => [
        inv.invoice_number, inv.party_name, inv.city, stageStyle(inv.status).label,
        inv.current_handler_name, inv.invoice_value,
        inv.received_at?.split('T')[0],
        isDelivered3(inv) ? (inv.article_count ?? '') : '',
        isDelivered3(inv) ? (inv.transport_name ?? '') : '',
      ]);
      return { headers, rows, textColumns: [] };
    }
  }

  function handleExport() {
    const filename = `DMS_${reportType}_${from}_to_${to}.xlsx`;
    const { headers, rows, textColumns } = buildReportData();
    downloadXLSX(headers, rows, filename, textColumns);
  }

  function handlePrint() {
    const { headers, rows } = buildReportData();
    const title = `DMS ${REPORT_TYPES.find(r => r.value === reportType)?.label || 'Report'} — ${from} to ${to}`;
    printReportA4(title, headers, rows);
  }

  function handleViewCSV() {
    const { headers, rows } = buildReportData();
    previewCSV(headers, rows);
  }

  const staffRoleColors = {
    owner: 'bg-red-100 text-red-700',
    team_leader: 'bg-purple-100 text-purple-700',
    stock_removal: 'bg-yellow-100 text-yellow-700',
    checking: 'bg-blue-100 text-blue-700',
    packing: 'bg-indigo-100 text-indigo-700',
    delivery_person: 'bg-green-100 text-green-700',
  };

  return (
    <div className="p-6 max-w-[1600px]">
      <PageHeader title="Reports" subtitle="Generate date-wise, party-wise, staff-wise and trip reports" />

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <div className="mb-5">
          <p className="text-sm font-semibold text-gray-600 mb-3">Report Type</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {REPORT_TYPES.map(rt => (
              <button key={rt.value} onClick={() => { setReportType(rt.value); setData(null); setGenerated(false); }}
                className={`p-3 rounded-xl border-2 text-left transition-all ${reportType === rt.value ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}>
                <p className="text-sm font-bold text-gray-800">{rt.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{rt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mb-5">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">From Date</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">To Date</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-end gap-2">
            {[['Today', today(), today()], ['Last 7', daysAgo(7), today()], ['Last 30', daysAgo(30), today()], ['This Month', localDateString(new Date()).slice(0,8)+'01', today()]].map(([label, f, t]) => (
              <button key={label} onClick={() => { setFrom(f); setTo(t); }}
                className="px-3 py-2 text-xs font-semibold bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200">
                {label}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
            <div className="flex gap-1">
              {[['','All'],['instation','🏙️ Instation'],['outstation','🚛 Outstation']].map(([val,lbl]) => (
                <button key={val} onClick={() => setDeliveryType(val)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors
                    ${deliveryType === val
                      ? 'bg-blue-900 text-white border-blue-900'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        </div>

        {reportType === 'party_wise' && (
          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-600 mb-2">
              Filter by Party Name <span className="text-xs font-normal text-gray-400">(leave empty to show all)</span>
            </label>
            <input type="text" value={partyNameFilter} onChange={e => setPartyNameFilter(e.target.value)}
              placeholder="e.g. Jeevan Medical"
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        )}

        {(reportType === 'staff_wise' || reportType === 'trip_report') && (
          <div className="mb-5">
            <p className="text-sm font-semibold text-gray-600 mb-2">
              Filter by Team Member <span className="text-xs font-normal text-gray-400">(leave empty to show all)</span>
            </p>
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 bg-gray-50 rounded-xl border border-gray-200">
              {users.filter(u => reportType === 'trip_report' ? u.role === 'delivery_person' : true).map(u => (
                <button key={u.id} onClick={() => toggleUser(u.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${selectedUsers.includes(u.id) ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'}`}>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${staffRoleColors[u.role] || 'bg-gray-100 text-gray-600'}`}>{u.role?.replace(/_/g, ' ')}</span>
                  {u.name}
                </button>
              ))}
            </div>
            {selectedUsers.length > 0 && (
              <button onClick={() => setSelectedUsers([])} className="mt-2 text-xs text-blue-600 hover:underline">
                Clear selection ({selectedUsers.length} selected)
              </button>
            )}
          </div>
        )}

        <button onClick={generate} disabled={loading}
          className="px-8 py-3 bg-blue-900 text-white rounded-xl font-bold text-sm hover:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed">
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Generating...
            </span>
          ) : '📊 Generate Report'}
        </button>
      </div>

      {loading && <div className="flex justify-center py-16"><Spinner size="lg" /></div>}

      {generated && !loading && data !== null && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">{REPORT_TYPES.find(r => r.value === reportType)?.label} Report</h2>
              <p className="text-sm text-gray-500">
                {formatDate(from)} → {formatDate(to)}
                {selectedUsers.length > 0 && ` · ${selectedUsers.length} member(s) selected`}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={handleViewCSV}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 font-semibold flex items-center gap-2">
                👁 View CSV
              </button>
              <button onClick={handlePrint}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 font-semibold flex items-center gap-2">
                🖨 Print (A4)
              </button>
              <button onClick={handleExport}
                className="px-4 py-2 bg-green-700 text-white text-sm rounded-lg hover:bg-green-600 font-semibold flex items-center gap-2">
                ⬇ Export XLSX
              </button>
            </div>
          </div>

          {reportType === 'trip_report' && (
            data.length === 0 ? <EmptyState /> : (
              <div className="space-y-4">
                {data.map((trip, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-4 bg-blue-50 border-b border-blue-100 flex flex-wrap justify-between items-start gap-3">
                      <div>
                        <p className="font-bold text-blue-900 text-lg">{trip.delivery_person_name} — Round #{trip.round_number}</p>
                        <p className="text-sm text-blue-600">{trip.delivery_person_phone} · {formatDate(trip.trip_date)}</p>
                      </div>
                      <div className="flex gap-3 flex-wrap">
                        <Stat label="Duration" value={formatDuration(trip.trip_duration_seconds)} color="blue" />
                        <Stat label="Invoices" value={trip.total_invoices} color="gray" />
                        <Stat label="Delivered" value={trip.delivered} color="green" />
                        <Stat label="Reattempt" value={trip.reattempts} color="amber" />
                        <Stat label="Cancelled" value={trip.cancelled} color="red" />
                      </div>
                    </div>
                    <div className="px-5 py-3 bg-gray-50 border-b flex gap-8 text-sm">
                      <div>
                        <span className="text-gray-400 text-xs">Round Start</span>
                        <p className="font-semibold text-gray-700">{formatDateTime(trip.trip_start)}</p>
                      </div>
                      <div>
                        <span className="text-gray-400 text-xs">Round End</span>
                        <p className="font-semibold text-gray-700">{trip.trip_end ? formatDateTime(trip.trip_end) : 'Still in progress'}</p>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {(trip.invoices || []).map((inv, j) => (
                        <div key={j} className="px-5 py-2.5 flex justify-between items-center text-sm">
                          <div>
                            <span className="font-mono font-semibold text-gray-800">{inv.invoice_number}</span>
                            <span className="text-gray-400 mx-2">·</span>
                            <span className="text-gray-600">{inv.party_name}</span>
                            <span className="text-gray-400 text-xs ml-2">{inv.city}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              inv.outcome === 'signed' || inv.outcome === 'neighbour' ? 'bg-green-100 text-green-700' :
                              inv.outcome === 'reattempt' ? 'bg-amber-100 text-amber-700' :
                              inv.outcome === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {inv.outcome}
                            </span>
                            {inv.voucher_returned && inv.voucher_status && (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                                {inv.voucher_status.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {reportType === 'date_wise' && (
            data.length === 0 ? <EmptyState /> : (
              <div className="space-y-4">
                {getDateWiseData().map(([date, invs]) => (
                  <div key={date} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b flex justify-between items-center">
                      <p className="font-bold text-gray-800">{formatDate(date)}</p>
                      <span className="text-sm text-gray-500">{invs.length} invoice{invs.length !== 1 ? 's' : ''} · {formatCurrency(invs.reduce((s, i) => s + (parseFloat(i.invoice_value) || 0), 0))}</span>
                    </div>
                    <InvoiceTable invoices={invs} sortCol={sortCol} sortOrder={sortOrder} onSort={handleSort} />
                  </div>
                ))}
              </div>
            )
          )}

          {reportType === 'party_wise' && (
            data.length === 0 ? <EmptyState /> : (
              <div className="space-y-4">
                {getPartyWiseData().map(([party, invs]) => (
                  <div key={party} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b flex justify-between items-center">
                      <div>
                        <p className="font-bold text-gray-800">{party}</p>
                        <p className="text-xs text-gray-400">{invs[0]?.city}</p>
                      </div>
                      <span className="text-sm text-gray-500">{invs.length} invoice{invs.length !== 1 ? 's' : ''} · {formatCurrency(invs.reduce((s, i) => s + (parseFloat(i.invoice_value) || 0), 0))}</span>
                    </div>
                    <InvoiceTable invoices={invs} sortCol={sortCol} sortOrder={sortOrder} onSort={handleSort} />
                  </div>
                ))}
              </div>
            )
          )}

          {reportType === 'staff_wise' && (
            data.length === 0 ? <EmptyState /> : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <InvoiceTable invoices={data} showHandler sortCol={sortCol} sortOrder={sortOrder} onSort={handleSort} />
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// Builds one combined WhatsApp message for a "parcel" — one or more
// outstation invoices for the same party that went out together, matching
// how the delivery app already groups same-party outstation invoices into
// a single card at handover time.
function buildWhatsAppLink(invGroup) {
  const first = invGroup[0];
  const phone = (first.party_phone || '').replace(/[^0-9]/g, '');
  if (!phone) return null;
  // Assume India (+91) if no country code already present — 10-digit
  // local numbers are the norm in this dataset (see party master).
  const fullPhone = phone.length === 10 ? `91${phone}` : phone;

  const totalArticles = invGroup.reduce((sum, i) => sum + (i.article_count || 0), 0);
  const invoiceNumbers = invGroup.map(i => i.invoice_number).join(', ');
  const totalValue = invGroup.reduce((sum, i) => sum + (parseFloat(i.invoice_value) || 0), 0);
  // Same courier/notes for the whole parcel in the normal case (handed
  // over together) — use the first non-empty value found in the group.
  const transportName = invGroup.find(i => i.transport_name)?.transport_name || '';
  const notes = invGroup.find(i => i.handover_notes)?.handover_notes || '';

  const lines = [
    `Hello ${first.party_name},`,
    ``,
    `Your parcel of ${totalArticles || '—'} article${totalArticles !== 1 ? 's' : ''} for`,
    `Invoice${invGroup.length > 1 ? 's' : ''} ${invoiceNumbers} worth ₹${totalValue.toLocaleString('en-IN')}`,
    `has been dispatched through ${transportName || 'our transport partner'}.`,
  ];
  if (notes) lines.push(notes);
  lines.push(``, `Regards,`, `Mangal Distributors`);

  const message = lines.join('\n');
  // wa.me (not web.whatsapp.com directly) — web.whatsapp.com only allows
  // one active session per browser tab; clicking a second WhatsApp button
  // while an earlier one is still open just refocuses that existing tab
  // instead of loading the new contact/message, forcing you to manually
  // close it between every single send. wa.me forces a fresh redirect
  // each time instead, so multiple parcels can be sent back-to-back.
  return `https://wa.me/${fullPhone}?text=${encodeURIComponent(message)}`;
}

function InvoiceTable({ invoices, showHandler, sortCol, sortOrder, onSort }) {
  const isDelivered = (inv) => ['stage_6_delivered', 'complete'].includes(inv.status);
  const COLUMNS = [
    { key: 'invoice_number', label: 'Invoice #' },
    { key: 'party_name',     label: 'Party' },
    { key: 'city',           label: 'City' },
    { key: 'status',         label: 'Stage' },
    showHandler ? { key: 'current_handler_name', label: 'Handler' } : null,
    { key: 'invoice_value',  label: 'Value' },
    { key: 'transport_name', label: 'Transport' },
    { key: 'received_at',    label: 'Date' },
    { key: '_whatsapp',      label: '' },
  ].filter(Boolean);

  function sortIcon(key) {
    if (sortCol !== key) return <span style={{ color: '#d1d5db', marginLeft: '4px' }}>↕</span>;
    return <span style={{ color: '#2563eb', marginLeft: '4px' }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  }

  const sorted = [...invoices].sort((a, b) => {
    if (!sortCol) return 0;
    let av = a[sortCol], bv = b[sortCol];
    if (sortCol === 'invoice_value') { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
    else if (sortCol === 'received_at') { av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0; }
    else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  // Group same-party outstation invoices into "parcels" — matches how
  // the delivery app already groups them into one card at handover time.
  // Grouping is computed from the full invoice list (not just what's
  // currently sorted/visible) so a parcel's WhatsApp message always
  // covers every invoice in it, regardless of sort order.
  const parcelGroups = {};
  invoices.forEach(inv => {
    if (inv.delivery_type !== 'outstation') return;
    const key = inv.party_id || `${inv.party_name}__${inv.city}`;
    if (!parcelGroups[key]) parcelGroups[key] = [];
    parcelGroups[key].push(inv);
  });
  // Which invoice_id is the "representative" row that shows the WhatsApp
  // button for its whole parcel — the lowest invoice_number in the group,
  // so it's stable regardless of table sort order.
  const parcelRepresentative = {};
  Object.values(parcelGroups).forEach(group => {
    const rep = [...group].sort((a, b) => (a.invoice_number || '').localeCompare(b.invoice_number || ''))[0];
    parcelRepresentative[rep.invoice_id] = group;
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {COLUMNS.map(c => (
              <th key={c.key} onClick={() => c.key !== '_whatsapp' && onSort(c.key)}
                className={`text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase select-none ${c.key !== '_whatsapp' ? 'cursor-pointer hover:bg-gray-100' : ''}`}>
                {c.label}{c.key !== '_whatsapp' && sortIcon(c.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((inv, i) => {
            const st = stageStyle(inv.status);
            const parcelGroup = parcelRepresentative[inv.invoice_id];
            const waLink = parcelGroup ? buildWhatsAppLink(parcelGroup) : null;
            return (
              <tr key={i} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2.5 font-mono font-semibold text-blue-900 text-xs">{inv.invoice_number}</td>
                <td className="px-4 py-2.5 font-medium text-gray-800 text-xs">{inv.party_name}</td>
                <td className="px-4 py-2.5 text-gray-500 text-xs">{inv.city}</td>
                <td className="px-4 py-2.5">
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: st.bg, color: st.text }}>{st.label}</span>
                </td>
                {showHandler && <td className="px-4 py-2.5 text-gray-500 text-xs">{inv.current_handler_name || '—'}</td>}
                <td className="px-4 py-2.5 font-medium text-xs">{formatCurrency(inv.invoice_value)}</td>
                <td className="px-4 py-2.5 text-gray-500 text-xs">{isDelivered(inv) ? (inv.transport_name || '—') : '—'}</td>
                <td className="px-4 py-2.5 text-gray-400 text-xs">{inv.received_at ? new Date(inv.received_at).toLocaleDateString('en-IN') : '—'}</td>
                <td className="px-4 py-2.5">
                  {waLink && (
                    <button
                      onClick={() => window.open(waLink, 'dms_whatsapp_sender', 'width=420,height=680')}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-100"
                      title={parcelGroup.length > 1
                        ? `Send one combined update for all ${parcelGroup.length} invoices in this parcel: ${parcelGroup.map(x => x.invoice_number).join(', ')}`
                        : 'Send shipment update on WhatsApp'}>
                      💬 WhatsApp{parcelGroup.length > 1 ? ` (${parcelGroup.length})` : ''}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, color }) {
  const colors = { blue: 'bg-blue-100 text-blue-800', green: 'bg-green-100 text-green-800', amber: 'bg-amber-100 text-amber-800', red: 'bg-red-100 text-red-800', gray: 'bg-gray-100 text-gray-700' };
  return (
    <div className={`px-3 py-1.5 rounded-lg text-center ${colors[color]}`}>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-xs">{label}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-16 text-center">
      <p className="text-4xl mb-3">📊</p>
      <p className="text-gray-500 font-medium">No data found for this period</p>
      <p className="text-gray-400 text-sm mt-1">Try adjusting the date range or filters</p>
    </div>
  );
}
